import { Document, Page } from "react-pdf";
import type { DocumentProps } from "react-pdf";
import { Group } from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { usePdf } from "./use-pdf";
import { Move } from "@morph-mapper/ui";
import { useStore } from "../../context";
import { useTextSelect } from "../../hooks";
import { useTextHighlight } from "../../hooks/use-text-highlight";
import { Footer } from "../../molecules";
import { FileViewContent, FileViewHeader, FileViewWrapper, TextContent } from "./styles";
import { useMemo, useCallback, useEffect, useRef } from "react";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

export const PdfView = () => {
  const file = useStore(({ file: f }) => f.getFile());
  const { ref, width } = useElementSize();
  const {
    numPages,
    pageNumber,
    onDocumentLoadSuccess,
    previousPage,
    nextPage,
    pageText,
  } = usePdf();
  const highlights = useStore(({ file: f }) => f.highlights);
  const highlightOccurrenceTargetMap = new Map<string, number>();
  const highlightOccurenceCurrentMap = new Map<string, number>();
  const textContent = useStore(({ file: f }) => f.textContent);

  for (const highlight of highlights) {
    if (highlight.head >= 0 && highlight.tail <= textContent.length && highlight.head <= highlight.tail) {
      const textBeforeHighlight = textContent.slice(0, highlight.head);
      const occurrencesBefore = (textBeforeHighlight.match(new RegExp(escapeRegExp(highlight.txt), 'g')) || []).length;
      const key = `${highlight.head}-${highlight.tail}-${highlight.color}-${highlight.txt}`;
      highlightOccurrenceTargetMap.set(key, occurrencesBefore);
    } else {
      console.warn('Invalid highlight bounds:', highlight);
    }
  }

  function escapeRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  useTextSelect();

  // Stabilize onDocumentLoadSuccess so pageLayer memo doesn't invalidate on unrelated renders
  type OnDocLoad = NonNullable<DocumentProps['onLoadSuccess']>;
  const onLoadRef = useRef<OnDocLoad | undefined>(onDocumentLoadSuccess as OnDocLoad | undefined);
  useEffect(() => {
    onLoadRef.current = onDocumentLoadSuccess as OnDocLoad | undefined;
  }, [onDocumentLoadSuccess]);
  const stableOnDocumentLoadSuccess: OnDocLoad = useCallback((...args: Parameters<OnDocLoad>) => {
    return onLoadRef.current ? onLoadRef.current(...args) : undefined as unknown as ReturnType<OnDocLoad>;
  }, []);

  const pageLayer = useMemo(() => {
    return (
      <FileViewContent ref={ref}>
        <Document file={file} onLoadSuccess={stableOnDocumentLoadSuccess}>
          <Page
            width={width}
            pageNumber={pageNumber}
            renderTextLayer={true}
            renderAnnotationLayer={true}
            customTextRenderer={({ str }) => {
              let result = str;
              // Debug per text chunk
              // This log can be verbose; keep for diagnosis
              // console.log('[PdfView] customTextRenderer chunk', { len: str.length });

              highlights.forEach(highlight => {
                if (str.includes(highlight.txt)) {
                  const key = `${highlight.head}-${highlight.tail}-${highlight.color}-${highlight.txt}`;

                  const targetOccurrence = highlightOccurrenceTargetMap.get(key) ?? 0;

                  const currentCount = highlightOccurenceCurrentMap.get(key) ?? 0;

                  if (currentCount === targetOccurrence) {
                    result = result.replace(
                      new RegExp(escapeRegExp(highlight.txt), 'g'),
                      (match) => {
                        if (currentCount === targetOccurrence) {
                          return `<mark style="background-color: ${highlight.color};">${match}</mark>`;
                        }
                        return match;
                      }
                    );
                  }

                  const occurrencesInStr = (str.match(new RegExp(escapeRegExp(highlight.txt), 'g'))?.length || 0);
                  highlightOccurenceCurrentMap.set(key, currentCount + occurrencesInStr);
                }
              });

              return result;
            }}
          />
        </Document>
      </FileViewContent>
    );
  }, [file, width, pageNumber, highlights, textContent]);

  return (
    <FileViewWrapper>
      <FileViewHeader>
        <Group>
          <Move
            into={false}
            onClick={previousPage}
            disabled={pageNumber <= 1}
          />
          {pageNumber} / {numPages}
          <Move onClick={nextPage} disabled={pageNumber >= numPages} />
        </Group>
      </FileViewHeader>
      {pageLayer}
      <Footer />
    </FileViewWrapper>
  );
};
